import React, { useMemo, useState } from "react";
import {
  Send,
  CheckCircle,
  AlertTriangle,
  User,
  Phone,
  Mail,
  Briefcase,
  MessageSquare,
  Loader2
} from "lucide-react";

/**
 * Netlify Forms helper: URL-encode body
 */
const encode = (data) =>
  Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");

const ApplyForm = () => {
  // ✅ Where your mailto link goes (Netlify Forms emails are configured in Netlify > Forms)
  const applyEmail = "info@agentletouchhomecarellc1.com";
  const formName = "apply";

  const initialState = useMemo(
    () => ({
      applicantName: "",
      phone: "",
      email: "",
      employmentType: "",
      message: "",
      "bot-field": "" // honeypot MUST exist
    }),
    []
  );

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [statusMsg, setStatusMsg] = useState("");

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.applicantName.trim()) newErrors.applicantName = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(formData.email)) newErrors.email = "Please enter a valid email";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setStatusMsg("");
    if (!validateForm()) {
      setStatus("error");
      setStatusMsg("Please fix the highlighted fields and try again.");
      return;
    }

    setStatus("submitting");
    setStatusMsg("Submitting your application…");

    try {
      // ✅ POST to current route (works well with Netlify + SPA redirects)
      const response = await fetch(window.location.pathname, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": formName,
          ...formData
        })
      });

      // Netlify sometimes responds with 200 or 302 depending on config
      if (!response.ok && response.status !== 302) {
        throw new Error(`Netlify submission failed: ${response.status}`);
      }

      setStatus("success");
      setStatusMsg("✅ Application received! We’ll be in touch soon.");
      setFormData(initialState);
      setErrors({});
    } catch (err) {
      console.error("Form submit error:", err);
      setStatus("error");
      setStatusMsg("Sorry — something went wrong. Please try again in a moment.");
    }
  };

  const isSubmitting = status === "submitting";

  return (
    <div className="w-full">
      {/* ✅ IMPORTANT: Netlify detection stub (needed for React/SPAs) */}
      <form name={formName} data-netlify="true" netlify-honeypot="bot-field" hidden>
        <input type="text" name="applicantName" />
        <input type="email" name="email" />
        <input type="tel" name="phone" />
        <select name="employmentType">
          <option value="full-time">Full time</option>
          <option value="part-time">Part time</option>
        </select>
        <textarea name="message" />
        <input name="bot-field" />
      </form>

      {/* Contact Info */}
      <div className="mb-6 space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4" style={{ color: "#c4a57e" }} />
          <a href="tel:16149284169" className="underline" style={{ color: "#c4a57e" }}>
            614.928.4169
          </a>
        </div>

        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4" style={{ color: "#c4a57e" }} />
          <a href={`mailto:${applyEmail}`} className="underline" style={{ color: "#c4a57e" }}>
            {applyEmail}
          </a>
        </div>
      </div>

      {/* Status message (shows something when you submit) */}
      {status !== "idle" && statusMsg && (
        <div
          className="mb-5 rounded-xl border p-4 text-sm flex items-start gap-3"
          style={{
            borderColor: status === "success" ? "#c4a57e" : status === "error" ? "#fca5a5" : "#e5e7eb",
            background: status === "success" ? "rgba(196,165,126,0.08)" : "rgba(0,0,0,0.02)"
          }}
          role="status"
          aria-live="polite"
        >
          {status === "success" ? (
            <CheckCircle className="w-5 h-5 mt-0.5" style={{ color: "#c4a57e" }} />
          ) : status === "error" ? (
            <AlertTriangle className="w-5 h-5 mt-0.5 text-red-500" />
          ) : (
            <Loader2 className="w-5 h-5 mt-0.5 animate-spin" style={{ color: "#c4a57e" }} />
          )}
          <div className="flex-1">{statusMsg}</div>
          {status === "success" && (
            <button
              type="button"
              onClick={() => {
                setStatus("idle");
                setStatusMsg("");
              }}
              className="text-xs underline"
              style={{ color: "#c4a57e" }}
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      <form
        name={formName}
        method="POST"
        data-netlify="true"
        netlify-honeypot="bot-field"
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        {/* Required for Netlify */}
        <input type="hidden" name="form-name" value={formName} />

        {/* Honeypot (must be a real input named bot-field) */}
        <p className="hidden">
          <label>
            Don’t fill this out:{" "}
            <input name="bot-field" value={formData["bot-field"]} onChange={handleChange} />
          </label>
        </p>

        <div>
          <label htmlFor="applicantName" className="form-label flex items-center gap-2">
            <User className="w-4 h-4" style={{ color: "#c4a57e" }} />
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="applicantName"
            name="applicantName"
            value={formData.applicantName}
            onChange={handleChange}
            className={`form-input ${errors.applicantName ? "border-red-400" : ""}`}
            placeholder="Enter your full name"
            autoComplete="name"
          />
          {errors.applicantName && <p className="text-red-500 text-sm mt-1">{errors.applicantName}</p>}
        </div>

        <div>
          <label htmlFor="applyEmail" className="form-label flex items-center gap-2">
            <Mail className="w-4 h-4" style={{ color: "#c4a57e" }} />
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="applyEmail"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`form-input ${errors.email ? "border-red-400" : ""}`}
            placeholder="your.email@example.com"
            autoComplete="email"
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="applyPhone" className="form-label flex items-center gap-2">
            <Phone className="w-4 h-4" style={{ color: "#c4a57e" }} />
            Phone
          </label>
          <input
            type="tel"
            id="applyPhone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="form-input"
            placeholder="(555) 123-4567"
            autoComplete="tel"
          />
        </div>

        <div>
          <label htmlFor="employmentType" className="form-label flex items-center gap-2">
            <Briefcase className="w-4 h-4" style={{ color: "#c4a57e" }} />
            Position Interest
          </label>
          <select
            id="employmentType"
            name="employmentType"
            value={formData.employmentType}
            onChange={handleChange}
            className="form-input"
          >
            <option value="">Select position type</option>
            <option value="full-time">Full time</option>
            <option value="part-time">Part time</option>
          </select>
        </div>

        <div>
          <label htmlFor="applyMessage" className="form-label flex items-center gap-2">
            <MessageSquare className="w-4 h-4" style={{ color: "#c4a57e" }} />
            Message
          </label>
          <textarea
            id="applyMessage"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={3}
            className="form-input resize-none"
            placeholder="Tell us about yourself and your experience..."
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-full font-medium transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            background: "linear-gradient(135deg, #c4a57e 0%, #a88a5f 100%)",
            color: "white"
          }}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Submitting…
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Submit Application
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default ApplyForm;

